import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Plus, X, Paperclip, Lock, CheckCircle2, ArrowLeft, UserPlus, AlertTriangle } from "lucide-react";

const TABS = [["details", "Visit details"], ["activities", "Activities"], ["outcomes", "Outcomes and follow-up"],
  ["attach", "Attachments and notes"], ["review", "Review and submit"]];

function SaveLabel({ state, savedAt, onRetry }) {
  const txt = state === "failed" ? "Not saved — connection or server error"
    : state === "saving" ? "Saving…"
    : state === "dirty" ? "Unsaved changes…"
    : savedAt ? `Saved ${savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "All changes saved";
  return (
    <span className={`text-xs font-bold flex items-center gap-2 ${state === "failed" ? "text-[#991B1B]" : "text-[#78716C]"}`}
      data-testid="save-state-label">
      {txt}
      {state === "failed" && <button className="btn-ghost !py-0.5 !px-2 text-xs" onClick={onRetry} data-testid="save-retry-button">Retry</button>}
    </span>
  );
}

export default function VisitWork() {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [v, setV] = useState(null);
  const [tab, setTab] = useState("details");
  const [contracts, setContracts] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [busy, setBusy] = useState("");
  const [saveState, setSaveState] = useState("saved");
  const [savedAt, setSavedAt] = useState(null);
  const [pn, setPn] = useState(null);
  const [pnState, setPnState] = useState("saved");
  const [newVisit, setNewVisit] = useState({ school_id: "", date: new Date().toISOString().slice(0, 10) });
  const [reflTeacher, setReflTeacher] = useState("");
  const [link, setLink] = useState({ label: "", url: "" });
  const pending = useRef({});
  const timer = useRef(null);
  const flushing = useRef(false);
  const pnTimer = useRef(null);

  useEffect(() => { api.get("/visits/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(apiError(e))); }, []);

  useEffect(() => {
    if (!visitId) return;
    api.get(`/visits/${visitId}`).then((r) => {
      setV(r.data);
      if (r.data.is_owner) api.get(`/visits/${visitId}/private-notes`).then((p) => setPn(p.data.text)).catch(() => setPn(""));
    }).catch((e) => { toast.error(apiError(e)); navigate("/logs"); });
  }, [visitId]);

  useEffect(() => {
    if (v?.school_id && v?.date) api.get(`/contracts/for-school/${v.school_id}?date=${v.date}`)
      .then((r) => setContracts(r.data)).catch(() => setContracts([]));
    else setContracts([]);
  }, [v?.school_id, v?.date]);

  const flush = useCallback(async () => {
    if (flushing.current || !visitId) return;
    const payload = pending.current;
    if (!Object.keys(payload).length) return;
    pending.current = {};
    flushing.current = true;
    setSaveState("saving");
    try {
      const { data } = await api.patch(`/visits/${visitId}`, payload);
      setV((cur) => ({ ...cur, ...data }));
      setSavedAt(new Date());
      setSaveState("saved");
    } catch (e) {
      pending.current = { ...payload, ...pending.current }; // entered text is preserved; retry offered
      setSaveState("failed");
    } finally {
      flushing.current = false;
    }
  }, [visitId]);

  const edit = (fields) => {
    setV((cur) => ({ ...cur, ...fields }));
    pending.current = { ...pending.current, ...fields };
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 900);
  };

  const editPn = (text) => {
    setPn(text);
    setPnState("dirty");
    clearTimeout(pnTimer.current);
    pnTimer.current = setTimeout(async () => {
      setPnState("saving");
      try { await api.put(`/visits/${visitId}/private-notes`, { text }); setPnState("saved"); }
      catch { setPnState("failed"); }
    }, 900);
  };

  const createVisit = async () => {
    setBusy("create");
    try {
      const { data } = await api.post("/visits", newVisit);
      navigate(`/visit-work/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const organize = async () => {
    await flush();
    setBusy("organize");
    try {
      const { data } = await api.post(`/visits/${visitId}/organize`);
      setProposal(data);
      if (!data.rows.length && !data.missing.length) toast("Nothing usable was found in the notes.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const applyProposal = async () => {
    setBusy("apply");
    try {
      const { data } = await api.post(`/visits/${visitId}/organize/apply`, { rows: proposal.rows });
      setV((cur) => ({ ...cur, ...data }));
      setProposal(null);
      toast.success("Applied. Check every field; nothing has been submitted.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const addReflection = async () => {
    if (!reflTeacher.trim()) return;
    setBusy("refl");
    try {
      const { data } = await api.post(`/visits/${visitId}/reflections`, { teacher: reflTeacher.trim() });
      navigate(`/reflections/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(`/visits/${visitId}/attachments?label=${encodeURIComponent(f.name)}`, fd);
      edit({ attachments: [...(v.attachments || []), data] });
      toast.success("Attachment uploaded.");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); e.target.value = ""; }
  };

  const submit = async () => {
    await flush();
    setBusy("submit");
    try {
      await api.post(`/visits/${visitId}/submit`);
      toast.success("Submitted for approval.");
      navigate(`/visits/${visitId}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  if (!meta) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="visit-work-loading">Loading…</div>;

  if (!visitId) {
    return (
      <div className="max-w-xl space-y-6" data-testid="new-visit-page">
        <div>
          <p className="eyebrow">CKingEducation Daily Visit Digital Log</p>
          <h1 className="section-title text-3xl mt-1">New Daily Visit Log</h1>
          <p className="text-sm text-[#57534E] mt-1">You are identified automatically from your account. Choose the school and date — everything saves as a draft as you go.</p>
        </div>
        <div className="card p-6 space-y-4">
          <div>
            <label className="label">School (your assigned schools)</label>
            <select className="input" value={newVisit.school_id} data-testid="new-visit-school"
              onChange={(e) => setNewVisit({ ...newVisit, school_id: e.target.value })}>
              <option value="">Select a school…</option>
              {meta.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Visit date</label>
            <input type="date" className="input" value={newVisit.date} data-testid="new-visit-date"
              onChange={(e) => setNewVisit({ ...newVisit, date: e.target.value })} />
          </div>
          <button className="btn-primary" onClick={createVisit} disabled={busy === "create"} data-testid="new-visit-start">
            Start visit log</button>
        </div>
      </div>
    );
  }

  if (!v) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="visit-loading">Loading visit…</div>;
  const readOnly = v.status !== "draft";
  const fmtElapsed = (m) => (m == null ? "—" : `${Math.floor(m / 60)}h ${m % 60}m`);
  const setAct = (i, fields) => edit({ activities: v.activities.map((a, j) => (j === i ? { ...a, ...fields } : a)) });
  const contract = contracts.find((c) => c.id === v.contract_id);

  return (
    <div className="max-w-5xl space-y-5" data-testid="visit-work-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/logs" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1" data-testid="back-to-logs"><ArrowLeft size={14} /> Visit Logs</Link>
          <h1 className="font-serif font-bold text-xl text-navy" data-testid="visit-title">{v.school?.name || "New visit"} · {v.date}</h1>
        </div>
        <SaveLabel state={saveState} savedAt={savedAt} onRetry={flush} />
      </div>
      {readOnly && (
        <div className="rounded-lg border border-[#E7E1D4] bg-sand/60 px-4 py-3 text-sm" data-testid="readonly-banner">
          This log is <strong>{v.computed_status}</strong>. <Link className="font-bold text-terracotta hover:underline" to={`/visits/${v.id}`}>Open the review page</Link> to withdraw or manage it.
        </div>
      )}
      {(v.billing_warnings || []).length > 0 && (
        <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] px-4 py-3 space-y-1" data-testid="billing-warnings">
          {v.billing_warnings.map((w, i) => (
            <div key={i} className="text-xs text-[#92400E] flex gap-2"><AlertTriangle size={13} className="shrink-0 mt-0.5" />{w}</div>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b border-[#E7E1D4] overflow-x-auto" data-testid="visit-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
              tab === k ? "border-terracotta text-navy" : "border-transparent text-[#78716C] hover:text-navy"}`}>{l}</button>
        ))}
      </div>

      <fieldset disabled={readOnly} className="space-y-5">
        {tab === "details" && (
          <>
            <div className="card p-6 grid sm:grid-cols-2 gap-4">
              <div><label className="label">Consultant</label><input className="input bg-sand/40" value={v.consultant_name} disabled data-testid="visit-consultant" /></div>
              <div><label className="label">Visit date</label><input type="date" className="input" value={v.date} data-testid="visit-date" onChange={(e) => edit({ date: e.target.value })} /></div>
              <div><label className="label">School</label>
                <select className="input" value={v.school_id} data-testid="visit-school" onChange={(e) => edit({ school_id: e.target.value, contract_id: "" })}>
                  <option value="">Select…</option>
                  {meta.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select></div>
              <div><label className="label">Contract / Purchase Order</label>
                <select className="input" value={v.contract_id} data-testid="visit-contract" onChange={(e) => edit({ contract_id: e.target.value })}>
                  <option value="">None chosen</option>
                  {contracts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.po ? ` · PO ${c.po}` : ""}{!c.in_range ? " (outside dates)" : ""}</option>)}
                </select></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="label">Start</label><input type="time" className="input" value={v.start} data-testid="visit-start" onChange={(e) => edit({ start: e.target.value })} /></div>
                <div><label className="label">End</label><input type="time" className="input" value={v.end} data-testid="visit-end" onChange={(e) => edit({ end: e.target.value })} /></div>
                <div><label className="label">Breaks (min)</label><input type="number" min="0" className="input" value={v.break_minutes} data-testid="visit-breaks" onChange={(e) => edit({ break_minutes: e.target.value })} /></div>
              </div>
              <div><label className="label">Time on site (elapsed)</label><input className="input bg-sand/40" value={fmtElapsed(v.elapsed_minutes)} disabled data-testid="visit-elapsed" /></div>
              <div className="sm:col-span-2"><label className="label">Grade levels</label><input className="input" value={v.grades} placeholder="e.g. 3, 4–5" data-testid="visit-grades" onChange={(e) => edit({ grades: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className="label">Visit purpose / goals</label><textarea className="input min-h-[70px]" value={v.purpose} data-testid="visit-purpose" onChange={(e) => edit({ purpose: e.target.value })} /></div>
            </div>

            <div className="card p-6 space-y-3" data-testid="supported-section">
              <div className="flex items-center justify-between">
                <h3 className="font-serif font-bold text-navy">Teachers, teams, or school leaders supported</h3>
                <button className="btn-ghost text-xs" onClick={() => edit({ supported: [...(v.supported || []), { name: "", role: "Teacher", email: "" }] })} data-testid="add-supported"><Plus size={13} /> Add person</button>
              </div>
              {(v.supported || []).map((p, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`supported-row-${i}`}>
                  <input className="input !py-1.5 text-sm flex-1 min-w-[140px]" placeholder="Name or alias" value={p.name} data-testid={`supported-name-${i}`}
                    onChange={(e) => edit({ supported: v.supported.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                  <select className="input !py-1.5 text-sm" value={p.role} data-testid={`supported-role-${i}`}
                    onChange={(e) => edit({ supported: v.supported.map((x, j) => (j === i ? { ...x, role: e.target.value } : x)) })}>
                    {meta.people_roles.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <input className="input !py-1.5 text-sm flex-1 min-w-[160px]" placeholder="Email (optional)" value={p.email} data-testid={`supported-email-${i}`}
                    onChange={(e) => edit({ supported: v.supported.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)) })} />
                  <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => edit({ supported: v.supported.filter((_, j) => j !== i) })} data-testid={`supported-remove-${i}`}><X size={13} /></button>
                </div>
              ))}
            </div>

            <div className="card p-6 space-y-3" data-testid="billing-section">
              <h3 className="font-serif font-bold text-navy">Billable quantity <span className="text-xs font-normal text-[#78716C]">— separate from time on site; a full-day visit with several activities is still one day</span></h3>
              {!contract && <p className="text-xs text-[#92400E]" data-testid="billing-no-contract">Choose a contract above to add billable services. An Admin reviews rates before approval.</p>}
              {contract && (contract.services || []).map((s) => {
                const item = (v.billing?.items || []).find((i) => i.service_id === s.id);
                return (
                  <div key={s.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`service-row-${s.id}`}>
                    <label className="flex items-center gap-2 flex-1">
                      <input type="checkbox" checked={!!item} data-testid={`service-check-${s.id}`}
                        onChange={(e) => {
                          const items = e.target.checked
                            ? [...(v.billing?.items || []), { service_id: s.id, label: s.label, qty: 1, unit: s.unit }]
                            : (v.billing?.items || []).filter((i) => i.service_id !== s.id);
                          edit({ billing: { ...(v.billing || {}), items } });
                        }} />
                      <span className="font-bold text-navy">{s.label}</span>
                      <span className="text-xs text-[#78716C]">per {s.unit}</span>
                    </label>
                    {item && (
                      <span className="flex items-center gap-1">
                        <input type="number" min="0" step="0.5" className="input !py-1 !w-20 text-sm" value={item.qty} data-testid={`service-qty-${s.id}`}
                          onChange={(e) => edit({ billing: { ...v.billing, items: v.billing.items.map((i) => (i.service_id === s.id ? { ...i, qty: e.target.value } : i)) } })} />
                        <span className="text-xs text-[#78716C]">{s.unit}(s)</span>
                      </span>
                    )}
                  </div>
                );
              })}
              <label className="flex items-center gap-2 text-sm" data-testid="billing-confirmed-row">
                <input type="checkbox" checked={!!v.billing?.confirmed} data-testid="billing-confirmed"
                  onChange={(e) => edit({ billing: { ...(v.billing || {}), confirmed: e.target.checked } })} />
                <span>I confirm this billable quantity (not the elapsed time)</span>
              </label>
              <input className="input text-sm" placeholder="Billing note (optional)" value={v.billing?.note || ""} data-testid="billing-note"
                onChange={(e) => edit({ billing: { ...(v.billing || {}), note: e.target.value } })} />
            </div>

            <div className="card p-6 space-y-3" data-testid="raw-notes-section">
              <h3 className="font-serif font-bold text-navy">Rough notes <span className="text-xs font-normal text-[#78716C]">— paste or type; the AI can organize them into fields above for your review</span></h3>
              <textarea className="input min-h-[120px] font-mono text-sm" value={v.raw_notes} placeholder="Paste rough notes here…" data-testid="raw-notes"
                onChange={(e) => edit({ raw_notes: e.target.value })} />
              <button className="btn-outline" onClick={organize} disabled={busy === "organize" || !(v.raw_notes || "").trim()} data-testid="organize-button">
                <Sparkles size={15} /> {busy === "organize" ? "Organizing…" : "Organize notes into a draft"}</button>
              {proposal && (
                <div className="rounded-lg border-2 border-[#FCD34D] bg-[#FFFBEB] p-4 space-y-3" data-testid="organize-proposal">
                  <div className="text-sm font-bold text-navy">Draft from your notes <span className="badge-draft ml-2">Not applied</span></div>
                  <p className="text-xs text-[#57534E]">Tick what to use. Fields you already filled in are unticked, so nothing is overwritten unless you choose it.</p>
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-[#78716C]"><th className="text-left">Use</th><th className="text-left">Field</th><th className="text-left">From your notes</th></tr></thead>
                    <tbody>
                      {proposal.rows.map((r, i) => (
                        <tr key={i} data-testid={`proposal-row-${r.key}`}>
                          <td className="py-1">{!r.info && <input type="checkbox" checked={r.on} data-testid={`proposal-use-${r.key}`}
                            onChange={(e) => setProposal({ ...proposal, rows: proposal.rows.map((x, j) => (j === i ? { ...x, on: e.target.checked } : x)) })} />}</td>
                          <td className="font-bold">{r.label}</td>
                          <td className="text-[#57534E]">{r.display}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {proposal.missing.length > 0 && <p className="text-xs text-[#92400E]"><strong>Not in your notes:</strong> {proposal.missing.join(", ")}. Fill these in by hand; nothing was guessed.</p>}
                  {proposal.uncertain.length > 0 && <p className="text-xs text-[#78716C]"><strong>Unclear:</strong> {proposal.uncertain.join(" · ")}</p>}
                  <div className="flex gap-2">
                    <button className="btn-primary !py-1.5 text-xs" onClick={applyProposal} disabled={busy === "apply"} data-testid="proposal-apply">Apply selected</button>
                    <button className="btn-ghost !py-1.5 text-xs" onClick={() => setProposal(null)} data-testid="proposal-discard">Discard</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "activities" && (
          <div className="space-y-4" data-testid="activities-tab">
            <div className="flex items-center justify-between">
              <p className="text-sm text-[#57534E]">Multiple activity blocks within one visit — they never multiply the billable quantity by themselves.</p>
              <button className="btn-outline text-xs" data-testid="add-activity"
                onClick={() => edit({ activities: [...(v.activities || []), { id: Math.random().toString(36).slice(2, 10), type: "", start: "", end: "", participants: "", grades: "", description: "", evidence: "" }] })}>
                <Plus size={13} /> Add activity</button>
            </div>
            {(v.activities || []).map((a, i) => (
              <div key={a.id || i} className="card p-5 space-y-3" data-testid={`activity-${i}`}>
                <div className="grid sm:grid-cols-4 gap-3">
                  <div className="sm:col-span-2"><label className="label">Service type</label>
                    <select className="input" value={a.type} data-testid={`activity-type-${i}`} onChange={(e) => setAct(i, { type: e.target.value })}>
                      <option value="">Select…</option>
                      {meta.service_types.map((t) => <option key={t}>{t}</option>)}
                    </select></div>
                  <div><label className="label">Start</label><input type="time" className="input" value={a.start} data-testid={`activity-start-${i}`} onChange={(e) => setAct(i, { start: e.target.value })} /></div>
                  <div><label className="label">End</label><input type="time" className="input" value={a.end} data-testid={`activity-end-${i}`} onChange={(e) => setAct(i, { end: e.target.value })} /></div>
                  <div><label className="label">Participants</label><input className="input" value={a.participants} data-testid={`activity-participants-${i}`} onChange={(e) => setAct(i, { participants: e.target.value })} /></div>
                  <div><label className="label">Grade(s)</label><input className="input" value={a.grades} data-testid={`activity-grades-${i}`} onChange={(e) => setAct(i, { grades: e.target.value })} /></div>
                  <div className="sm:col-span-2 flex items-end justify-end">
                    <button className="btn-ghost text-[#991B1B] text-xs" onClick={() => edit({ activities: v.activities.filter((_, j) => j !== i) })} data-testid={`activity-remove-${i}`}><X size={13} /> Remove</button>
                  </div>
                </div>
                <div><label className="label">Description</label><textarea className="input min-h-[60px]" value={a.description} data-testid={`activity-description-${i}`} onChange={(e) => setAct(i, { description: e.target.value })} /></div>
                <div><label className="label">Outcome / evidence</label><input className="input" value={a.evidence} data-testid={`activity-evidence-${i}`} onChange={(e) => setAct(i, { evidence: e.target.value })} /></div>
              </div>
            ))}
            {!(v.activities || []).length && <div className="card p-6 text-sm text-[#78716C]" data-testid="activities-empty">No activities yet — add one, or organize rough notes on the details tab.</div>}
          </div>
        )}

        {tab === "outcomes" && (
          <div className="space-y-4" data-testid="outcomes-tab">
            <div className="card p-6 space-y-4">
              <div><label className="label">Accomplishments (brief summary)</label>
                <textarea className="input min-h-[90px]" value={v.summary} data-testid="visit-summary" onChange={(e) => edit({ summary: e.target.value })} /></div>
              <div><label className="label">Evidence (artifacts, decisions, agreements)</label>
                <textarea className="input min-h-[60px]" value={v.evidence} data-testid="visit-evidence" onChange={(e) => edit({ evidence: e.target.value })} /></div>
            </div>
            <div className="card p-6 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-serif font-bold text-navy">Follow-up actions</h3>
                <button className="btn-ghost text-xs" onClick={() => edit({ follow_ups: [...(v.follow_ups || []), { text: "", owner: "Consultant", due: "" }] })} data-testid="add-followup"><Plus size={13} /> Add follow-up</button>
              </div>
              {(v.follow_ups || []).map((f, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`followup-row-${i}`}>
                  <input className="input !py-1.5 text-sm flex-1 min-w-[200px]" placeholder="Action" value={f.text} data-testid={`followup-text-${i}`}
                    onChange={(e) => edit({ follow_ups: v.follow_ups.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} />
                  <select className="input !py-1.5 text-sm" value={f.owner} data-testid={`followup-owner-${i}`}
                    onChange={(e) => edit({ follow_ups: v.follow_ups.map((x, j) => (j === i ? { ...x, owner: e.target.value } : x)) })}>
                    {meta.follow_owners.map((o) => <option key={o}>{o}</option>)}
                  </select>
                  <input type="date" className="input !py-1.5 text-sm" value={f.due} data-testid={`followup-due-${i}`}
                    onChange={(e) => edit({ follow_ups: v.follow_ups.map((x, j) => (j === i ? { ...x, due: e.target.value } : x)) })} />
                  <button className="btn-ghost !py-1 text-[#991B1B]" onClick={() => edit({ follow_ups: v.follow_ups.filter((_, j) => j !== i) })} data-testid={`followup-remove-${i}`}><X size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "attach" && (
          <div className="space-y-4" data-testid="attach-tab">
            <div className="card p-6 space-y-3">
              <h3 className="font-serif font-bold text-navy">Attachments</h3>
              {(v.attachments || []).map((a, i) => (
                <div key={a.id || i} className="flex items-center gap-2 text-sm" data-testid={`attachment-row-${i}`}>
                  <Paperclip size={13} className="text-terracotta" />
                  {a.storage_path
                    ? <a className="font-bold text-navy hover:underline" href={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/attachments/${a.id}/file`} target="_blank" rel="noreferrer">{a.label || a.filename}</a>
                    : <a className="font-bold text-navy hover:underline break-all" href={a.url} target="_blank" rel="noreferrer">{a.label || a.url}</a>}
                  <button className="btn-ghost !py-0.5 text-[#991B1B]" onClick={() => edit({ attachments: v.attachments.filter((_, j) => j !== i) })} data-testid={`attachment-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <input className="input !py-1.5 text-xs w-40" placeholder="Label" value={link.label} data-testid="attachment-label" onChange={(e) => setLink({ ...link, label: e.target.value })} />
                <input className="input !py-1.5 text-xs flex-1 min-w-[200px]" placeholder="https://… (Google Drive, OneDrive…)" value={link.url} data-testid="attachment-url" onChange={(e) => setLink({ ...link, url: e.target.value })} />
                <button className="btn-ghost !py-1.5 text-xs" data-testid="attachment-add"
                  onClick={() => { if (link.url.startsWith("https://")) { edit({ attachments: [...(v.attachments || []), { label: link.label, url: link.url }] }); setLink({ label: "", url: "" }); } else toast.error("Links must start with https://"); }}>Add link</button>
                <label className="btn-outline !py-1.5 text-xs cursor-pointer" data-testid="attachment-upload-label">
                  Upload file<input type="file" className="hidden" onChange={uploadFile} data-testid="attachment-upload" /></label>
              </div>
            </div>
            {v.is_owner && pn !== null && (
              <div className="card p-6 space-y-2 border-l-4 border-l-navy" data-testid="private-notes-card">
                <h3 className="font-serif font-bold text-navy flex items-center gap-2"><Lock size={15} /> Private consultant notes</h3>
                <p className="text-xs text-[#78716C]">Only you can read these — not administrators, not the school. They are never included in summaries, exports, or invoices.</p>
                <textarea className="input min-h-[110px]" value={pn} data-testid="private-notes" onChange={(e) => editPn(e.target.value)} />
                <span className={`text-xs font-bold ${pnState === "failed" ? "text-[#991B1B]" : "text-[#78716C]"}`} data-testid="private-save-state">
                  {pnState === "saving" ? "Saving…" : pnState === "failed" ? "Not saved — keep this page open and retype or retry" : pnState === "dirty" ? "Unsaved changes…" : "Saved"}</span>
              </div>
            )}
          </div>
        )}

        {tab === "review" && (
          <div className="space-y-4" data-testid="review-tab">
            <div className="card p-6 space-y-3">
              <h3 className="font-serif font-bold text-navy">Ready to submit?</h3>
              <ul className="space-y-1">
                {(v.validation || []).map((c, i) => (
                  <li key={i} className={`text-sm flex items-center gap-2 ${c.ok ? "text-[#166534]" : "text-[#991B1B]"}`} data-testid={`validation-${i}`}>
                    {c.ok ? <CheckCircle2 size={14} /> : <X size={14} />} {c.label}
                  </li>
                ))}
              </ul>
              {v.amount != null && <p className="text-sm">Billing estimate: <strong data-testid="billing-amount">${v.amount.toFixed(2)}</strong> <span className="text-xs text-[#78716C]">(reviewed by an Admin at approval)</span></p>}
              <div className="flex flex-wrap gap-2">
                <button className="btn-primary" onClick={submit} disabled={busy === "submit" || !(v.validation || []).every((c) => c.ok)} data-testid="submit-visit">
                  <Send size={14} /> {busy === "submit" ? "Submitting…" : "Submit for approval"}</button>
                <a className="btn-outline" href={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/export`} target="_blank" rel="noreferrer" data-testid="export-html">Preview school-facing summary</a>
                <a className="btn-ghost" href={`${process.env.REACT_APP_BACKEND_URL}/api/visits/${v.id}/export.pdf`} target="_blank" rel="noreferrer" data-testid="export-pdf">PDF</a>
              </div>
              <p className="text-xs text-[#78716C]">The school-facing summary excludes private notes, coaching content, billing, and student names.</p>
            </div>
            <div className="card p-6 space-y-3" data-testid="reflections-section">
              <h3 className="font-serif font-bold text-navy flex items-center gap-2"><UserPlus size={15} /> Teacher coaching summaries (TRU)</h3>
              <p className="text-xs text-[#57534E]">Optional. One reflection per teacher — observation notes, debrief, and teacher-facing feedback with AI assistance and your review before anything is shared.</p>
              {(v.reflections || []).map((r) => (
                <div key={r.id} className="flex items-center gap-2 text-sm" data-testid={`reflection-row-${r.id}`}>
                  <Link className="font-bold text-terracotta hover:underline" to={`/reflections/${r.id}`}>{r.teacher || "Teacher summary"}</Link>
                  {r.has_feedback && <span className="badge-approved">Feedback drafted</span>}
                  {(r.share?.sent_status === "sent") && <span className="badge-neutral">Sent</span>}
                </div>
              ))}
              <div className="flex gap-2">
                <input className="input !py-1.5 text-sm flex-1 max-w-xs" placeholder="Teacher name or alias" value={reflTeacher} data-testid="new-reflection-teacher" onChange={(e) => setReflTeacher(e.target.value)} />
                <button className="btn-outline !py-1.5 text-xs" onClick={addReflection} disabled={busy === "refl" || !reflTeacher.trim()} data-testid="add-reflection"><Plus size={13} /> Start teacher summary</button>
              </div>
            </div>
            <div className="card p-6" data-testid="history-card">
              <h3 className="font-serif font-bold text-navy mb-2">History</h3>
              <ul className="space-y-1">{(v.history || []).map((h, i) => (
                <li key={i} className="text-xs text-[#57534E]" data-testid={`history-${i}`}><strong>{h.by || "—"}</strong> · {h.what} · {new Date(h.at).toLocaleString()}</li>
              ))}</ul>
            </div>
          </div>
        )}
      </fieldset>
    </div>
  );
}
